import entraIdAuth from "./entraIdAuth";
import traditionalAuth from "./traditionalAuth";
import entraIdBackendAuth from "./entraIdBackendAuth";
import * as msal from "@azure/msal-browser";

class AuthService {
  constructor() {
    this.authMethod = localStorage.getItem("authMethod") || null;
    this.authProviders = {
      entraId: entraIdAuth,
      traditional: traditionalAuth,
    };
    // Race condition prevention for token refresh
    this.isRefreshingBackendToken = false;
    this.refreshPromise = null;
  }

  getCurrentAuthMethod() {
    return this.authMethod;
  }

  setAuthMethod(method) {
    this.authMethod = method;
    localStorage.setItem("authMethod", method);
  }

  async loginWithEntraId(usePopup = true) {
    try {
      let msalResult;
      if (usePopup) {
        msalResult = await entraIdAuth.loginWithPopup();
      } else {
        await entraIdAuth.loginWithRedirect();
        return { success: true, redirect: true };
      }

      if (msalResult.success) {
        // Extract ID token and email from MSAL result
        const idToken = msalResult.idToken;
        const email = msalResult.profile?.mail || msalResult.account?.username;

        if (!idToken) {
          return {
            success: false,
            error: "ID_TOKEN_MISSING",
            message: "Failed to obtain ID token from Microsoft",
          };
        }

        if (!email) {
          return {
            success: false,
            error: "EMAIL_MISSING",
            message: "Failed to obtain email from Microsoft account",
          };
        }

        console.log("🔐 MSAL login successful, verifying with backend...");

        // Verify with backend (pass full msalResult for better identity extraction)
        const backendResult = await entraIdBackendAuth.verifyWithBackend({
          idToken,
          email,
          msalResult,
        });

        if (backendResult.success) {
          console.log("💾 Storing backend result...");

          this.setAuthMethod("entraId");

          // Store essential info in localStorage
          localStorage.setItem(
            "msalAccount",
            JSON.stringify(msalResult.account),
          );
          localStorage.setItem(
            "userProfile",
            JSON.stringify(msalResult.profile),
          );

          // CRITICAL: Store backend-issued JWT token (NOT MSAL access token)
          localStorage.setItem("jwtToken", backendResult.token);

          // Store backend token expiry if available
          if (backendResult.tokenExpiry) {
            localStorage.setItem(
              "backendTokenExpiresAt",
              backendResult.tokenExpiry.toString(),
            );
            console.log(
              "⏰ Backend token expires at:",
              new Date(backendResult.tokenExpiry).toISOString(),
            );
          }

          // Store user data from backend (matches traditional login structure)
          if (backendResult.user) {
            localStorage.setItem(
              "currentUser",
              JSON.stringify(backendResult.user),
            );
          }

          // Store MSAL token expiration for tracking (from MSAL for UI purposes)
          if (msalResult.expiresAt) {
            localStorage.setItem(
              "tokenExpiresAt",
              msalResult.expiresAt.toString(),
            );
          }

          // Note: MSAL v2 handles automatic token refresh internally via acquireTokenSilent
          // No need for custom timer-based refresh logic

          console.log("✅ Entra ID login successful");

          return {
            success: true,
            account: msalResult.account,
            profile: msalResult.profile,
            user: backendResult.user,
            token: backendResult.token,
            code: backendResult.code,
            message: backendResult.message,
          };
        } else {
          // Backend verification failed
          console.error("Backend verification failed:", backendResult.error);

          // Clear MSAL session if backend verification fails
          await entraIdAuth.logout();

          return {
            success: false,
            error: backendResult.error,
            message: backendResult.message,
          };
        }
      }

      return msalResult;
    } catch (error) {
      console.error("Entra ID login failed:", error);
      return {
        success: false,
        error: "LOGIN_ERROR",
        message: error.message || "An error occurred during login",
      };
    }
  }

  /**
   * Login with Entra ID using retry logic for stuck interaction state
   * This is the enhanced version that automatically recovers from interaction_in_progress errors
   *
   * @param {boolean} usePopup - Whether to use popup (true) or redirect (false)
   * @returns {Promise<Object>} Login result with potential suggestRedirect flag
   */
  async loginWithEntraIdWithRetry(usePopup = true) {
    try {
      let msalResult;
      if (usePopup) {
        // Use the retry-enabled login method
        msalResult = await entraIdAuth.loginWithPopupAndRetry();
      } else {
        await entraIdAuth.loginWithRedirect();
        return { success: true, redirect: true };
      }

      // Check if we should suggest redirect fallback
      if (msalResult.suggestRedirect) {
        console.warn("⚠️ Popup login exhausted retries, suggesting redirect fallback");
        return {
          success: false,
          error: msalResult.error,
          message: msalResult.message,
          suggestRedirect: true,
        };
      }

      if (msalResult.success) {
        // Extract ID token and email from MSAL result
        const idToken = msalResult.idToken;
        const email = msalResult.profile?.mail || msalResult.account?.username;

        if (!idToken) {
          return {
            success: false,
            error: "ID_TOKEN_MISSING",
            message: "Failed to obtain ID token from Microsoft",
          };
        }

        if (!email) {
          return {
            success: false,
            error: "EMAIL_MISSING",
            message: "Failed to obtain email from Microsoft account",
          };
        }

        console.log("🔐 MSAL login successful, verifying with backend...");

        // Verify with backend (pass full msalResult for better identity extraction)
        const backendResult = await entraIdBackendAuth.verifyWithBackend({
          idToken,
          email,
          msalResult,
        });

        if (backendResult.success) {
          console.log("💾 Storing backend result...");

          this.setAuthMethod("entraId");

          // Store essential info in localStorage
          localStorage.setItem(
            "msalAccount",
            JSON.stringify(msalResult.account),
          );
          localStorage.setItem(
            "userProfile",
            JSON.stringify(msalResult.profile),
          );

          // CRITICAL: Store backend-issued JWT token (NOT MSAL access token)
          localStorage.setItem("jwtToken", backendResult.token);

          // Store backend token expiry if available
          if (backendResult.tokenExpiry) {
            localStorage.setItem(
              "backendTokenExpiresAt",
              backendResult.tokenExpiry.toString(),
            );
            console.log(
              "⏰ Backend token expires at:",
              new Date(backendResult.tokenExpiry).toISOString(),
            );
          }

          // Store user data from backend (matches traditional login structure)
          if (backendResult.user) {
            localStorage.setItem(
              "currentUser",
              JSON.stringify(backendResult.user),
            );
          }

          // Store MSAL token expiration for tracking (from MSAL for UI purposes)
          if (msalResult.expiresAt) {
            localStorage.setItem(
              "tokenExpiresAt",
              msalResult.expiresAt.toString(),
            );
          }

          // Note: MSAL v2 handles automatic token refresh internally via acquireTokenSilent
          // No need for custom timer-based refresh logic

          console.log("✅ Entra ID login successful");

          return {
            success: true,
            account: msalResult.account,
            profile: msalResult.profile,
            user: backendResult.user,
            token: backendResult.token,
            code: backendResult.code,
            message: backendResult.message,
          };
        } else {
          // Backend verification failed
          console.error("Backend verification failed:", backendResult.error);

          // Clear MSAL session if backend verification fails
          await entraIdAuth.logout();

          return {
            success: false,
            error: backendResult.error,
            message: backendResult.message,
          };
        }
      }

      return msalResult;
    } catch (error) {
      console.error("Entra ID login with retry failed:", error);
      return {
        success: false,
        error: "LOGIN_ERROR",
        message: error.message || "An error occurred during login",
      };
    }
  }

  async loginWithCredentials(credentials) {
    try {
      const result = await traditionalAuth.login(credentials);

      if (result.success) {
        this.setAuthMethod("traditional");
      }

      return result;
    } catch (error) {
      console.error("Traditional login failed:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Refresh backend JWT token using fresh MSAL token
   * This prevents "Session expired" errors when backend JWT expires
   * while MSAL session is still valid
   *
   * @param {boolean} useCachedMsalToken - Use cached MSAL token instead of forcing refresh
   * @returns {Promise<Object>} Result with new token or error
   */
  async refreshBackendToken(useCachedMsalToken = false) {
    // Race condition prevention - if already refreshing, return existing promise
    if (this.isRefreshingBackendToken && this.refreshPromise) {
      console.log("⏳ Backend token refresh already in progress, waiting...");
      return this.refreshPromise;
    }

    this.isRefreshingBackendToken = true;

    // Create promise that other callers can wait on
    this.refreshPromise = (async () => {
      const startTime = Date.now();
      try {
        console.log("🔄 Refreshing backend JWT token...");

        // Ensure MSAL is initialized before accessing msalInstance
        await entraIdAuth.ensureInitialized();

        const account = entraIdAuth.getActiveAccount();
        if (!account) {
          throw new Error("No active MSAL account found");
        }

        // For ID token, make a separate silent request with openid scope
        // Use RefreshTokenAndNetwork policy for guaranteed fresh token when needed
        const idTokenRequest = {
          scopes: ["openid", "profile", "email"],
          account: account,
          forceRefresh: !useCachedMsalToken,
          cacheLookupPolicy: useCachedMsalToken
            ? msal.CacheLookupPolicy.Default
            : msal.CacheLookupPolicy.RefreshTokenAndNetwork,
        };

        const idTokenResponse = await entraIdAuth.msalInstance.acquireTokenSilent(idTokenRequest);
        const idToken = idTokenResponse.idToken;

        if (!idToken) {
          throw new Error("No ID token available after MSAL refresh");
        }

        console.log("✅ Fresh MSAL tokens acquired");

        // Get user profile for identity extraction
        const profile = await entraIdAuth.getUserProfile();

        // Build MSAL result for identity extraction
        const msalResult = {
          account,
          profile,
          accessToken: idTokenResponse.accessToken,
          idToken,
        };

        // Call backend verification endpoint with fresh tokens
        const backendResult = await entraIdBackendAuth.verifyWithBackend({
          idToken,
          email: profile?.mail || account.username,
          msalResult,
          isRefresh: true, // Flag this as a refresh operation
        });

        if (backendResult.success) {
          const duration = Date.now() - startTime;
          console.log(`✅ Backend token refreshed successfully in ${duration}ms`);

          // Update stored backend JWT token
          localStorage.setItem("jwtToken", backendResult.token);

          // Update token expiry if available
          if (backendResult.tokenExpiry) {
            localStorage.setItem(
              "backendTokenExpiresAt",
              backendResult.tokenExpiry.toString(),
            );
            console.log(
              "⏰ Backend token expires at:",
              new Date(backendResult.tokenExpiry).toISOString(),
            );
          }

          // Update user data if changed
          if (backendResult.user) {
            localStorage.setItem(
              "currentUser",
              JSON.stringify(backendResult.user),
            );
          }

          return {
            success: true,
            token: backendResult.token,
            tokenExpiry: backendResult.tokenExpiry,
            duration,
          };
        } else {
          console.error(
            "❌ Backend token refresh failed:",
            backendResult.error,
          );

          return {
            success: false,
            error: backendResult.error,
            message: backendResult.message,
          };
        }
      } catch (error) {
        const duration = Date.now() - startTime;
        console.error(`❌ Backend token refresh error after ${duration}ms:`, error);
        return {
          success: false,
          error: "REFRESH_ERROR",
          message: error.message || "Failed to refresh backend token",
          duration,
        };
      } finally {
        // Always clear the refresh flag and promise
        this.isRefreshingBackendToken = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Check if backend token needs refresh based on expiry time
   * @returns {boolean} True if token should be refreshed
   */
  shouldRefreshBackendToken() {
    try {
      const expiryStr = localStorage.getItem("backendTokenExpiresAt");
      if (!expiryStr) {
        return false; // No expiry set, let backend handle it
      }

      const expiry = parseInt(expiryStr, 10);
      const now = Date.now();
      const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

      return now >= expiry - bufferTime;
    } catch (error) {
      console.error("Error checking backend token expiry:", error);
      return false;
    }
  }

  async logout() {
    try {
      const currentAuthMethod =
        this.authMethod || localStorage.getItem("authMethod");
      console.log("🚪 Logging out user (auth method:", currentAuthMethod, ")");

      if (currentAuthMethod === "entraId") {
        console.log("🔴 Clearing Entra ID session...");
        await entraIdAuth.logout();
      } else if (currentAuthMethod === "traditional") {
        console.log("🔴 Clearing traditional auth session...");
        await traditionalAuth.logout();
      }

      // Clear all auth-related data
      this.clearAllAuth();

      console.log("✅ Logout complete - all auth data cleared");
      return { success: true };
    } catch (error) {
      console.error("❌ Logout failed:", error);
      // Force clear even if logout fails
      this.clearAllAuth();
      console.log("⚠️ Force cleared auth data after logout error");
      return { success: true };
    }
  }

  clearAllAuth() {
    console.log("🧹 Clearing all authentication data...");
    this.authMethod = null;
    localStorage.removeItem("authMethod");
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("msalAccount");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("tokenExpiresAt");
    localStorage.removeItem("backendTokenExpiresAt");

    // Clear other auth-related data
    localStorage.removeItem("prevURL");

    // Clear refresh state
    this.isRefreshingBackendToken = false;
    this.refreshPromise = null;

    console.log("✅ All auth data cleared from localStorage");
  }

  async getToken() {
    try {
      // Ensure authMethod is loaded from localStorage if not set
      if (!this.authMethod) {
        this.authMethod = localStorage.getItem("authMethod");
      }

      if (!this.authMethod) {
        console.warn("⚠️ No auth method set, cannot get token");
        return null;
      }

      // For Entra ID auth, return the backend JWT token (NOT the MSAL token)
      // The backend JWT is stored in localStorage after backend verification
      if (this.authMethod === "entraId") {
        const backendToken = localStorage.getItem("jwtToken");
        if (!backendToken) {
          console.error(
            "❌ No backend JWT token found in localStorage for Entra ID auth",
          );

          // Clear inconsistent auth state to force fresh login
          this.clearAllAuth();
          await entraIdAuth.clearAuthState();

          return null;
        }
        return backendToken;
      } else if (this.authMethod === "traditional") {
        return await traditionalAuth.getToken();
      }

      return null;
    } catch (error) {
      console.error("Failed to get token:", error);
      throw error;
    }
  }

  async refreshToken() {
    try {
      if (!this.authMethod) {
        throw new Error("No authentication method set");
      }

      if (this.authMethod === "entraId") {
        // For Entra ID, refresh the backend JWT token (NOT the MSAL access token)
        // The backend JWT is what's used for API calls, not the MSAL token
        const result = await this.refreshBackendToken(true); // Use cached MSAL token for speed
        if (result.success) {
          return result.token; // Return backend JWT token
        } else {
          throw new Error(result.message || "Backend token refresh failed");
        }
      } else if (this.authMethod === "traditional") {
        return await traditionalAuth.refreshToken();
      }

      throw new Error("Invalid authentication method");
    } catch (error) {
      console.error("Token refresh failed:", error);
      throw error;
    }
  }

  isAuthenticated() {
    if (!this.authMethod) {
      return false;
    }

    if (this.authMethod === "entraId") {
      return entraIdAuth.isAuthenticated();
    } else if (this.authMethod === "traditional") {
      return traditionalAuth.isAuthenticated();
    }

    return false;
  }

  getCurrentUser() {
    if (!this.authMethod) {
      return null;
    }

    if (this.authMethod === "entraId") {
      const account = entraIdAuth.getActiveAccount();
      const profile = localStorage.getItem("userProfile");

      if (account) {
        return {
          ...account,
          profile: profile ? JSON.parse(profile) : null,
          authMethod: "entraId",
        };
      }
      return null;
    } else if (this.authMethod === "traditional") {
      const user = traditionalAuth.getCurrentUser();
      return user ? { ...user, authMethod: "traditional" } : null;
    }

    return null;
  }

  async hasRequiredScopes(scopes) {
    if (this.authMethod === "entraId") {
      return await entraIdAuth.hasRequiredScopes(scopes);
    } else if (this.authMethod === "traditional") {
      // Traditional auth doesn't use scopes, check permissions instead
      return scopes.every((scope) => traditionalAuth.hasPermission(scope));
    }
    return false;
  }

  async hasRequiredRoles(roles) {
    if (this.authMethod === "entraId") {
      return await entraIdAuth.hasRequiredRoles(roles);
    } else if (this.authMethod === "traditional") {
      return roles.some((role) => traditionalAuth.hasRole(role));
    }
    return false;
  }

  // Handle redirect promise for Entra ID (call this on app initialization)
  async handleRedirectPromise() {
    console.log("📞 handleRedirectPromise called - starting MSAL redirect processing...");
    try {
      const msalResult = await entraIdAuth.handleRedirectPromise();
      console.log("📋 MSAL handleRedirectPromise result:", {
        hasResult: !!msalResult,
        success: msalResult?.success,
        hasAccount: !!msalResult?.account,
        email: msalResult?.account?.username
      });

      if (msalResult && msalResult.success) {
        // Get user profile after successful redirect
        const profile = await entraIdAuth.getUserProfile();
        const email = profile?.mail || msalResult.account?.username;

        // Get ID token from MSAL response
        // In MSAL v2, if this is from redirect, we need to get the ID token
        // Check if msalResult has idToken directly, otherwise get it from a silent request
        let idToken = msalResult.idToken;

        if (!idToken) {
          console.log("⚠️ No idToken in msalResult, acquiring via silent request...");
          const idTokenRequest = {
            scopes: ["openid", "profile", "email"],
            account: msalResult.account,
            forceRefresh: true,
          };
          const idTokenResponse = await entraIdAuth.msalInstance.acquireTokenSilent(idTokenRequest);
          idToken = idTokenResponse.idToken;
          console.log("✅ ID token acquired via silent request");
        }

        if (!idToken) {
          console.error("❌ No ID token available after redirect");
          return {
            success: false,
            error: "ID_TOKEN_MISSING",
            message: "Failed to obtain ID token from Microsoft",
          };
        }

        if (!email) {
          console.error("❌ No email available after redirect");
          return {
            success: false,
            error: "EMAIL_MISSING",
            message: "Failed to obtain email from Microsoft account",
          };
        }

        console.log("🔐 MSAL redirect successful, verifying with backend...");
        console.log("📧 User email:", email);
        console.log("🎫 ID token length:", idToken?.length);

        // Build full msalResult for better identity extraction
        const fullMsalResult = {
          ...msalResult,
          profile,
        };

        console.log("🚀 Calling backend API to verify Entra ID token and get JWT...");
        // Verify with backend (pass full msalResult for better identity extraction)
        const backendResult = await entraIdBackendAuth.verifyWithBackend({
          idToken,
          email,
          msalResult: fullMsalResult,
        });
        console.log("✅ Backend API call completed:", {
          success: backendResult.success,
          hasToken: !!backendResult.token,
          hasUser: !!backendResult.user
        });

        if (backendResult.success) {
          this.setAuthMethod("entraId");
          localStorage.setItem(
            "msalAccount",
            JSON.stringify(msalResult.account),
          );

          // Store backend-issued JWT token
          localStorage.setItem("jwtToken", backendResult.token);

          // Store backend token expiry if available
          if (backendResult.tokenExpiry) {
            localStorage.setItem(
              "backendTokenExpiresAt",
              backendResult.tokenExpiry.toString(),
            );
            console.log(
              "⏰ Backend token expires at:",
              new Date(backendResult.tokenExpiry).toISOString(),
            );
          }

          // Store user data from backend
          if (backendResult.user) {
            localStorage.setItem(
              "currentUser",
              JSON.stringify(backendResult.user),
            );
          }

          if (profile) {
            localStorage.setItem("userProfile", JSON.stringify(profile));
          }

          // Note: MSAL v2 handles automatic token refresh internally via acquireTokenSilent
          // No need for custom timer-based refresh logic

          return {
            success: true,
            account: msalResult.account,
            profile,
            user: backendResult.user,
            token: backendResult.token,
          };
        } else {
          // Backend verification failed
          console.error(
            "Backend verification failed after redirect:",
            backendResult.error,
          );

          // Clear MSAL session if backend verification fails
          await entraIdAuth.logout();

          return {
            success: false,
            error: backendResult.error,
            message: backendResult.message,
          };
        }
      }
      return null;
    } catch (error) {
      console.error("Handle redirect failed:", error);
      return {
        success: false,
        error: "REDIRECT_ERROR",
        message: error.message || "An error occurred during redirect handling",
      };
    }
  }

  // Check if token is valid - MSAL v2 handles refresh automatically
  async validateToken() {
    try {
      if (!this.authMethod) {
        return false;
      }

      if (this.authMethod === "entraId") {
        // MSAL v2 automatically validates and refreshes tokens via acquireTokenSilent
        // Just check if user is authenticated
        return entraIdAuth.isAuthenticated();
      } else if (this.authMethod === "traditional") {
        if (traditionalAuth.isTokenExpired()) {
          try {
            await traditionalAuth.refreshToken();
            return true;
          } catch (error) {
            return false;
          }
        }
        return true;
      }

      return false;
    } catch (error) {
      console.error("Token validation failed:", error);
      return false;
    }
  }

  /**
   * Initialize auth service and perform page load token refresh
   * Call this on app startup to ensure fresh tokens
   */
  async initializeAuth() {
    try {
      console.log("🔧 Initializing auth service...");

      // Load auth method from localStorage if not set
      if (!this.authMethod) {
        this.authMethod = localStorage.getItem("authMethod");
        console.log("📋 Auth method from localStorage:", this.authMethod);
      }

      // Initialize MSAL and restore session for Entra ID
      if (this.authMethod === "entraId") {
        console.log("🔐 Entra ID auth detected - initializing MSAL...");

        // Initialize MSAL instance (critical for session restoration)
        await entraIdAuth.initializeMsal();
        console.log("✅ MSAL initialized");

        // Check if we have an existing MSAL account
        const accounts = entraIdAuth.msalInstance?.getAllAccounts() || [];
        console.log("👥 MSAL accounts found:", accounts.length);

        if (accounts.length > 0) {
          console.log("✅ Existing MSAL session found for:", accounts[0].username);

          // Set the active account
          entraIdAuth.account = accounts[0];
          entraIdAuth.msalInstance.setActiveAccount(accounts[0]);

          // Check if we have a backend JWT token
          const backendToken = localStorage.getItem("jwtToken");
          const backendExpiry = localStorage.getItem("backendTokenExpiresAt");

          console.log("🎫 Backend token status:", {
            hasToken: !!backendToken,
            hasExpiry: !!backendExpiry,
            expiry: backendExpiry ? new Date(parseInt(backendExpiry)).toISOString() : null
          });

          // If backend token is missing or expired, refresh it
          if (!backendToken || (backendExpiry && parseInt(backendExpiry) < Date.now())) {
            console.log("⚠️ Backend token missing or expired - refreshing...");
            try {
              const refreshResult = await this.refreshBackendToken(false); // Force fresh MSAL token
              if (refreshResult.success) {
                console.log("✅ Backend token refreshed successfully");
              } else {
                console.error("❌ Backend token refresh failed:", refreshResult.error);
                // Clear auth state if refresh fails
                this.clearAllAuth();
                await entraIdAuth.clearAuthState();
                return false;
              }
            } catch (refreshError) {
              console.error("❌ Backend token refresh error:", refreshError);
              // Clear auth state if refresh fails
              this.clearAllAuth();
              await entraIdAuth.clearAuthState();
              return false;
            }
          } else {
            console.log("✅ Valid backend token exists");
            // Optionally refresh tokens on page load for better UX
            await entraIdAuth.refreshTokensOnPageLoad();
          }
        } else {
          console.log("ℹ️ No existing MSAL session - user needs to login");
        }
      } else if (this.authMethod === "traditional") {
        console.log("🔐 Traditional auth detected");
        // Traditional auth is already initialized via localStorage
      } else {
        console.log("ℹ️ No auth method set - user not logged in");
      }

      console.log("✅ Auth service initialization complete");
      return true;
    } catch (error) {
      console.error("❌ Auth initialization failed:", error);
      return false;
    }
  }
}

export default new AuthService();
